import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import {
  openBuyPremiumFlowEvent,
  openExtendPremiumFlowEvent,
  openPremiumCurrencyDetailsEvent,
} from '@root/entities/premium'
import {
  ActivePremiumListItem,
  ActivePremiumListItemSkeleton,
  ExpiredPremiumListItem,
  ExpiredPremiumListItemSkeleton,
} from '@root/entities/premium/ui/desktop'
import {
  userPremiumsModelAtom,
  userPremiumTabAtom,
} from '@root/features/premium'
import { FadeOverflow } from '@root/shared/ui-kit'

import { premiumCurrenciesStateAtom } from '../atoms'

import { UserPremiumListEmpty } from './user-premium-list-empty'
import { UserPremiumListError } from './user-premium-list-error'

import type { CurrencyModel } from '@root/entities/currency'
import type { ClassNameProps } from '@root/shared/react'

const placeholders = Array.range(1, 3)

type UserPremiumListProps = {
  placement?: 'profile' | 'premium'
} & ClassNameProps

export const UserPremiumList: React.FC<UserPremiumListProps> = ({
  className,
  placement = 'premium',
}) => {
  const [model] = useAtom(userPremiumsModelAtom)
  const [currenciesState] = useAtom(premiumCurrenciesStateAtom)
  const loadUserPremiums = useAction(model.run)
  const openPremiumCurrencyDetails = useAction(openPremiumCurrencyDetailsEvent)
  const openBuyPremiumDrawer = useAction(openBuyPremiumFlowEvent)
  const openExtendPremiumDrawer = useAction(openExtendPremiumFlowEvent)
  const [tab] = useAtom(userPremiumTabAtom)

  const handleCurrency = (currency: CurrencyModel) => {
    openPremiumCurrencyDetails({ currencyAddress: currency.address })
  }

  return AsyncStateN.matchWaiting(currenciesState, {
    Success: (currencies) => {
      if (currencies.length === 0) {
        return <UserPremiumListEmpty placement={placement} tab={tab} />
      }

      return (
        <Root mode="always" variant="vertical" className={className}>
          {currencies.map((meta) => {
            if (tab === 'active') {
              return (
                <ActivePremiumListItem
                  key={meta.currencyAddress}
                  currency={meta.currency}
                  onClick={handleCurrency}
                  onEdit={(x) => handleCurrency(x.currency)}
                  onExtend={openExtendPremiumDrawer}
                  premium={meta}
                />
              )
            }

            return (
              <ExpiredPremiumListItem
                key={meta.currencyAddress}
                currency={meta.currency}
                premium={meta}
                onBuyAgain={(currency) => openBuyPremiumDrawer(currency)}
              />
            )
          })}
        </Root>
      )
    },
    Error: () => (
      <UserPremiumListError
        onRetry={loadUserPremiums}
        placement={placement}
        tab={tab}
      />
    ),
    Waiting: () => (
      <Root mode="always" variant="vertical" className={className}>
        {placeholders.map((it) =>
          tab === 'active' ? (
            <ActivePremiumListItemSkeleton key={it} />
          ) : (
            <ExpiredPremiumListItemSkeleton key={it} />
          ),
        )}
      </Root>
    ),
  })
}

const Root = styled(FadeOverflow)`
  display: grid;
  gap: 10px;
  padding-block: 6px 15px;
  padding-inline: 12px;
  overflow-y: auto;
  align-content: start;
`
